import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import type { AcceleratorVideoSource } from '../../services/api';

interface Props {
  source: AcceleratorVideoSource;
}

const HLS_MIME = 'application/vnd.apple.mpegurl';

/** Turns a proxy failure status into something the viewer can act on. */
function messageForStatus(status: number | undefined): string {
  if (status === 422) return 'This video is DRM-protected on Vimeo, so it can only play in Vimeo’s own player.';
  if (status === 401) return 'The video link expired. Close and reopen the video.';
  if (status === 403) return 'This video link is not a Vimeo link the app can stream.';
  return 'The video could not be loaded right now.';
}

/**
 * Vimeo's own embedded player for a saved Vimeo link, or null when the link
 * has no video id. Accepts vimeo.com/<id>[/<hash>] and player.vimeo.com/video/<id>?h=<hash>.
 */
export function vimeoEmbedUrl(sourceUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(sourceUrl);
  } catch {
    return null;
  }
  if (!/(^|\.)vimeo\.com$/i.test(url.hostname)) return null;
  const segments = url.pathname.split('/').filter(Boolean);
  const idIndex = segments.findIndex((segment) => /^\d+$/.test(segment));
  if (idIndex < 0) return null;
  const next = segments[idIndex + 1];
  const hash = url.searchParams.get('h') || (next && /^[0-9a-f]{6,}$/i.test(next) ? next : null);
  const params = new URLSearchParams({ autoplay: '1', title: '0', byline: '0', portrait: '0', dnt: '1' });
  if (hash) params.set('h', hash);
  return `https://player.vimeo.com/video/${segments[idIndex]}?${params.toString()}`;
}

/**
 * Plays a Super Accelerator video through the publicWebScrape proxy.
 * HLS uses hls.js where Media Source Extensions exist (Chrome, Android
 * WebView) and the native player elsewhere (iOS Safari / WKWebView);
 * progressive files go straight into <video>.
 *
 * When the proxy cannot stream the video (embed privacy limited to specific
 * domains without a VIMEO_ACCESS_TOKEN, or DRM), the frame switches to
 * Vimeo's own embedded player, which plays wherever the video's embed
 * allowlist permits.
 */
export default function AcceleratorVideoPlayer({ source }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const embedUrl = vimeoEmbedUrl(source.sourceUrl);
  const [useEmbed, setUseEmbed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    let cancelled = false;
    let destroy: (() => void) | null = null;
    setLoading(true);
    setError(null);
    setUseEmbed(false);

    const failWith = (message: string) => {
      if (cancelled) return;
      setLoading(false);
      if (embedUrl) setUseEmbed(true);
      else setError(message);
    };

    if (source.kind === 'mp4') {
      video.src = source.url;
    } else {
      import('hls.js')
        .then(({ default: Hls }) => {
          if (cancelled) return;
          if (Hls.isSupported()) {
            const hls = new Hls({ enableWorker: true });
            hls.on(Hls.Events.ERROR, (_event, data) => {
              if (!data.fatal) return;
              const status = (data.response as { code?: number } | undefined)?.code;
              hls.destroy();
              failWith(messageForStatus(status));
            });
            hls.loadSource(source.url);
            hls.attachMedia(video);
            destroy = () => hls.destroy();
          } else if (video.canPlayType(HLS_MIME)) {
            video.src = source.url;
          } else {
            failWith('This device cannot play streaming video.');
          }
        })
        .catch(() => failWith('The video player failed to load.'));
    }

    return () => {
      cancelled = true;
      destroy?.();
      video.removeAttribute('src');
      video.load();
    };
  }, [source, embedUrl]);

  if (useEmbed && embedUrl) {
    return (
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', bgcolor: '#000', borderRadius: 1.5, overflow: 'hidden' }}>
        <iframe
          src={embedUrl}
          title="Accelerator video"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', bgcolor: '#000', borderRadius: 1.5, overflow: 'hidden' }}>
      <video
        ref={videoRef}
        controls
        autoPlay
        playsInline
        onCanPlay={() => setLoading(false)}
        onError={handleNativeError}
        style={{ width: '100%', height: '100%', display: 'block', background: '#000' }}
      />
      {loading && !error && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
          <CircularProgress size={36} sx={{ color: '#f6c453' }} />
        </Box>
      )}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            p: 3,
            textAlign: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.85)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#fff', maxWidth: 360 }}>
            {error}
          </Typography>
          {source.sourceUrl && (
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => window.open(source.sourceUrl, '_blank', 'noopener')}
              sx={{ color: '#fff', borderColor: 'rgba(255, 255, 255, 0.5)' }}
            >
              Open in Vimeo
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  // A native <video> error (iOS HLS or mp4) carries no HTTP status.
  function handleNativeError() {
    setLoading(false);
    if (embedUrl) setUseEmbed(true);
    else setError(messageForStatus(undefined));
  }
}
